import { faCalendarAlt, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import moment from 'moment'
import { CalendarDateTemplateParams, CalendarProps } from 'primereact/calendar'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectRecentMilestoneChanges,
  updateE2EProjectLineFlexTaskGroupItemsDates,
  updateMilestoneRecentChange,
  updateMilestonesFromEditMilestoneDateResult,
} from '../../../../features/projectManagement/projectManagementSlice'
import useToastr from '../../../../hooks/useToastr'
import MilestoneDateService from '../../../../services/MilestoneDateService'
import { E2EProjectLineECDAdjustmentModel } from '../../../../shared/models/service-models/E2EProjectLineECDAdjustmentModel'
import ECDAdjustmentsConfirmationDialog from '../../shared/components/ecd-adjustments-confirmation-dialog/ECDAdjustmentsConfirmationDialog'
import ProjectStatus from '../../shared/enums/project-status'
import PmrE2EProjectFlexTaskMilestonesGroup from '../../shared/interfaces/pmr-e2e-project-milestone-group'
import classes from './MilestoneDateTemplate.module.scss'
import PmrEditableDateInput from '../../shared/components/pmr-editable-date-input/PmrEditableDateInput'
import { isValid } from 'date-fns'

type Props = {
  rowData: PmrE2EProjectFlexTaskMilestonesGroup
  dateClassName: (index: number) => string
  dateLabel: string
  fieldName: string
  isStartDate: boolean
}

const calendarStartDate = new Date(2020, 0, 1)
const calendarEndDate = new Date(2030, 11, 31)

const MilestoneDateTemplate = ({
  rowData,
  dateClassName,
  dateLabel,
  isStartDate,
  fieldName,
}: Props) => {
  const dispatch = useDispatch()
  const [viewDate, setViewDate] = useState<Date | undefined>()

  const [editModeMilestoneId, setEditModeMilestoneId] = useState<string | null>()
  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const [newDate, setNewDate] = useState<Date | null>(null)
  const [tempNewDate, setTempNewDate] = useState<Date | null>(null)
  const [hasError, setHasError] = useState(false)
  const [eCDAdjustmentsConfirmationDialogVisible, setECDAdjustmentsConfirmationDialogVisible] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isInlineLoading, setIsInlineLoading] = useState(false)
  const { showSuccess, showError } = useToastr()
  const [isECDAdjustmentsLoading, setIsECDAdjustmentsLoading] = useState(false)
  const [ecdAdjustments, setEcdAdjustments] = useState<E2EProjectLineECDAdjustmentModel | null>(
    null
  )
  const [isKeepECDDisabled, setIsKeepECDDisabled] = useState(false)
  const recentMilestoneChanges = useSelector(selectRecentMilestoneChanges)

  const isEditMode = (milestoneIndex: number) => {
    return editModeMilestoneId === rowData.id[milestoneIndex]
  }

  const setIsEditMode = (milestoneIndex: number, isEditMode: boolean) => {
    const milestoneId = rowData.id[milestoneIndex]
    setEditModeMilestoneId(isEditMode ? milestoneId : null)
  }

  const isEditable = (index: number) => {
    const milestoneStatus = rowData.status[index]
    let isAdhocRow = false

    if (rowData.isAdHoc) isAdhocRow = rowData.isAdHoc[index]

    if (isAdhocRow) {
      const predecessorTaskStatuses = rowData.predecessorTaskStatuses[index]

      if (predecessorTaskStatuses && predecessorTaskStatuses.length > 0 && isStartDate) {
        if (
          predecessorTaskStatuses.some(
            predecessor =>
              predecessor === ProjectStatus.InProgress ||
              predecessor === ProjectStatus.NotScheduled ||
              predecessor === ProjectStatus.NotStarted ||
              predecessor === ProjectStatus.AwaitingAssignment
          )
        )
          return false

        if (
          predecessorTaskStatuses.every(
            predecessor =>
              predecessor === ProjectStatus.Completed || predecessor === ProjectStatus.Cancelled
          )
        )
          return true
      }
    }

    return (
      milestoneStatus !== ProjectStatus.Completed && milestoneStatus !== ProjectStatus.Cancelled
    )
  }

  const dateTooltip = (index: number, date: string) => {
    const milestoneStatus = rowData.status[index]

    if (milestoneStatus === ProjectStatus.Completed)
      return `${dateLabel} of a Completed milestone cannot be updated/edited.`

    if (milestoneStatus === ProjectStatus.Cancelled)
      return 'Editing start and due dates, logging work hours, and timestamping are disabled for cancelled tasks/milestones.'

    return date
  }

  const dateTemplate = (e: CalendarDateTemplateParams) => {
    const date = new Date(e.year, e.month, e.day)
    const dayOfWeek = date.getDay()
    const isWeekend = dayOfWeek === 6 || dayOfWeek === 0

    return <span className={clsx(isWeekend && classes['not-allowed'])}>{e.day}</span>
  }

  const openEditDateCalendar = async (milestoneIndex: number) => {
    if (!isEditable(milestoneIndex)) return

    setIsCalendarVisible(false)
    setEditModeMilestoneId(null)

    const currentDate = rowData[fieldName][milestoneIndex]
    setIsEditMode(milestoneIndex, true)
    const newDate = new Date(currentDate)
    setNewDate(newDate)

    // Sets the view date into the first day of the month to address the issue linked below.
    // https://github.com/primefaces/primereact/issues/2948
    setViewDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1))

    setTimeout(() => {
      setIsCalendarVisible(true)
    }, 100)
  }

  const ecdAdjustmentsTableHeader = (
    <>
      Editing the {dateLabel.toLowerCase()} will trigger a recalibration of the schedule and ECD.
      <br /> <br /> How would you like to proceed?
    </>
  )

  const handleConfirm = async (adjustECDProjectLineIds: string[]) => {
    setIsLoading(true)
    const parsedNewDate = moment(newDate).format('YYYY-MM-DD')

    try {
      const model = {
        e2EProjectLineMilestoneId: editModeMilestoneId!,
        onlyFetchAdjustments: false,
        newStartDate: isStartDate ? parsedNewDate : null,
        newDueDate: !isStartDate ? parsedNewDate : null,
        isAdjustECD: adjustECDProjectLineIds.length > 0,
        isAdhocTask: false,
      }

      const result = isStartDate
        ? await MilestoneDateService.editMilestoneStartDate(model)
        : await MilestoneDateService.editDate(model)

      if (!result.isSuccess) return showError('Error', result.message)

      const message = `${dateLabel} for <b>'${result.data.milestoneName}'</b> under order line <b>'${result.data.orderLineDescription}'</b> has been successfully updated. Schedules has been adjusted accordingly.`
      showSuccess('Success!', message)
      setECDAdjustmentsConfirmationDialogVisible(false)
      setEditModeMilestoneId(null)

      dispatch(updateMilestonesFromEditMilestoneDateResult(result.data.updatedMilestones))
      dispatch(updateE2EProjectLineFlexTaskGroupItemsDates(result.data.updatedTasks))
      dispatch(updateMilestoneRecentChange(result.data.changes))
    } catch (ex: any) {
      showError('Error', 'There is an error processing your request. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAdhocDateOnChange = async (selectedDate: Date, index: number) => {
    setIsInlineLoading(true)
    setIsCalendarVisible(false)
    const parsedNewDate = selectedDate !== null ? moment(selectedDate).format('YYYY-MM-DD') : null
    const parseStartDate =
      rowData.startDate[index] !== null
        ? moment(rowData.startDate[index]).format('YYYY-MM-DD')
        : null
    const parseDueDate =
      rowData.dueDate[index] !== null ? moment(rowData.dueDate[index]).format('YYYY-MM-DD') : null

    try {
      const result = await MilestoneDateService.editDate({
        e2EProjectLineMilestoneId: editModeMilestoneId!,
        onlyFetchAdjustments: false,
        newStartDate: isStartDate ? parsedNewDate : parseStartDate,
        newDueDate: !isStartDate ? parsedNewDate : parseDueDate,
        isAdjustECD: false,
        isAdhocTask: true,
        isStartDateEdit: isStartDate,
      })

      if (!result.isSuccess) return showError('Error', result.message)

      const message = `${dateLabel} for <b>'${result.data.milestoneName}'</b> under order line <b>'${result.data.orderLineDescription}'</b> has been successfully updated. Schedules has been adjusted accordingly.`
      showSuccess('Success!', message)
      setEditModeMilestoneId(null)

      dispatch(updateMilestonesFromEditMilestoneDateResult(result.data.updatedMilestones))
      dispatch(updateE2EProjectLineFlexTaskGroupItemsDates(result.data.updatedTasks))
      dispatch(updateMilestoneRecentChange(result.data.changes))
    } catch (ex: any) {
      showError('Error', 'There is an error processing your request. Please try again later.')
    } finally {
      setIsInlineLoading(false)
    }
  }

  const applyTemporaryEcd = (selectedNewDate: Date | null | string, inputHasError: boolean) => {
    setHasError(inputHasError)

    if (typeof selectedNewDate === 'string' && !isValid(selectedNewDate)) {
      return
    }

    setTempNewDate(selectedNewDate as Date)
  }

  const handleNewDateChange = async (selectedNewDate: Date | null | string, index: number) => {
    let newEcd = selectedNewDate as Date
    let hasEcdChanges = false
    let isAdhocRow = false
    try {
      if (rowData[fieldName][index] === newDate) return
      if (hasError) return
      if (newEcd === null) return

      if (moment(selectedNewDate).format('YYYY-MM-DD') === moment(newDate).format('YYYY-MM-DD')) {
        setIsCalendarVisible(false)
        setEditModeMilestoneId(null)
        return
      }

      setNewDate(newEcd)

      if (rowData.isAdHoc) isAdhocRow = rowData.isAdHoc[index]

      if (isAdhocRow) handleAdhocDateOnChange(newEcd, index)
      else {
        setIsECDAdjustmentsLoading(true)
        setECDAdjustmentsConfirmationDialogVisible(true)
        const parsedNewDate = moment(newEcd).format('YYYY-MM-DD')
        if (isStartDate) {
          const ecdAdjustments = await MilestoneDateService.getEditMilestoneStartDateECDAdjustments(
            {
              e2EProjectLineMilestoneId: editModeMilestoneId!,
              newStartDate: parsedNewDate,
            }
          )

          setEcdAdjustments({
            adjustedECD: ecdAdjustments.data.newECD,
            currentECD: ecdAdjustments.data.currentECD,
            e2EProjectLineId: ecdAdjustments.data.e2EProjectLineId,
            orderlineDescription: ecdAdjustments.data.orderLineDescription,
            e2EProjectName: ecdAdjustments.data.e2EProjectName,
          })

          setIsECDAdjustmentsLoading(false)
          setIsKeepECDDisabled(ecdAdjustments.data.canKeepECD === false)
        } else {
          const ecdAdjustments = await MilestoneDateService.editDate({
            e2EProjectLineMilestoneId: editModeMilestoneId!,
            onlyFetchAdjustments: true,
            newStartDate: isStartDate ? parsedNewDate : null,
            newDueDate: !isStartDate ? parsedNewDate : null,
            isAdjustECD: false,
            isAdhocTask: false,
          })

          setEcdAdjustments({
            adjustedECD: ecdAdjustments.data.adjustedECD,
            currentECD: ecdAdjustments.data.oldECD,
            e2EProjectLineId: ecdAdjustments.data.e2EProjectLineId,
            orderlineDescription: ecdAdjustments.data.orderLineDescription,
            e2EProjectName: ecdAdjustments.data.e2EProjectName,
          })

          setIsECDAdjustmentsLoading(false)
          setIsKeepECDDisabled(ecdAdjustments.data.canKeepECD === false)
        }
      }
      hasEcdChanges = true
    } finally {
      setTempNewDate(null)
      if (!hasEcdChanges || isAdhocRow) setIsEditMode(index, false)
    }
  }

  const isRecentlyChanged = (milestoneIndex: number, fieldName: string) => {
    const milestoneId = rowData.id[milestoneIndex]

    return recentMilestoneChanges.some(
      change =>
        change.e2EProjectLineMilestoneId === milestoneId && change.fieldChanged === fieldName
    )
  }

  return (
    <>
      {rowData[fieldName].map((date: string, index: number) => {
        const isAdhocRow = rowData.isAdHoc ? rowData.isAdHoc[index] : false
        const startDate =
          rowData.startDate[index] === '' ? null : moment(rowData.startDate[index]).toDate()
        const dueDate =
          rowData.dueDate[index] === '' ? null : moment(rowData.dueDate[index]).toDate()
        const editableDateProps: CalendarProps = {
          minDate: startDate ?? calendarStartDate,
          maxDate: calendarEndDate,
        }

        if (isStartDate) {
          editableDateProps.minDate = calendarStartDate
          editableDateProps.maxDate = dueDate ?? calendarEndDate
        }

        let render = (
          <span
            className={clsx(
              isRecentlyChanged(index, isStartDate ? 'FormattedStartDate' : 'FormattedDueDate') &&
                'recently-changed',
              dateClassName(index),
              { 'gmawb-clickable-ui': isEditable(index) && isAdhocRow }
            )}
            onClick={() => {
              if (!isAdhocRow) return
              openEditDateCalendar(index)
            }}
          >
            {isAdhocRow && (
              <>
                {date === '' && isEditable(index) && (
                  <FontAwesomeIcon
                    className={classes['calendar-container']}
                    icon={faCalendarAlt}
                    title="Click to select a date or type in the format ‘'yyyy/mm/dd'’."
                  />
                )}

                {(date !== '' || !isEditable(index)) && (
                  <div style={{ cursor: 'pointer' }}>{date}</div>
                )}

                {isInlineLoading && (
                  <FontAwesomeIcon style={{ marginLeft: '0.5rem' }} icon={faSpinner} spin={true} />
                )}
              </>
            )}

            {!isAdhocRow && <>{date}</>}
          </span>
        )

        if (isEditMode(index)) {
          render = (
            <PmrEditableDateInput
              className={classes['calendar']}
              onEditDate={(selectedDate, inputHasError) =>
                applyTemporaryEcd(selectedDate, inputHasError)
              }
              value={tempNewDate ?? newDate!}
              hasError={hasError}
              yearRange={`${calendarStartDate.getFullYear()}:${calendarEndDate.getFullYear()}`}
              onHide={() => {
                handleNewDateChange(tempNewDate, index)
                setHasError(false)
              }}
              visible={isCalendarVisible}
              onVisibleChange={event => {
                setIsCalendarVisible(event.visible)
              }}
              {...editableDateProps}
            />
          )
        }

        return (
          <div
            key={index}
            title={dateTooltip(index, date)}
            className={clsx(
              { [classes['date-picker-div']]: !isAdhocRow },
              { [classes['date-picker-div-adhoc']]: isAdhocRow },
              {
                [classes['not-allowed']]: !isEditable(index),
                [classes['edit-mode']]: isEditMode(index),
              }
            )}
            style={{
              borderBottom: index + 1 !== rowData[fieldName].length ? '1px solid #ddd' : '',
            }}
          >
            <span>{render}</span>
          </div>
        )
      })}

      <ECDAdjustmentsConfirmationDialog
        isAdjustECDDisabled={isLoading}
        isKeepECDDisabled={isKeepECDDisabled || isLoading}
        isECDAdjustmentsLoading={isECDAdjustmentsLoading}
        tableHeader={ecdAdjustmentsTableHeader}
        customModalHeader="Estimated Completion Date (ECD)"
        customConfirmationMessage="Do you want to proceed?"
        forceProjectNameColumnDisplay={true}
        e2eProjectLineECDAdjustments={ecdAdjustments === null ? [] : [ecdAdjustments]}
        visible={eCDAdjustmentsConfirmationDialogVisible}
        onCancel={() => {
          setECDAdjustmentsConfirmationDialogVisible(false)
          setEditModeMilestoneId(null)
        }}
        onConfirm={handleConfirm}
        isKeepButtonVisible={true}
        isCancelButtonVisible={false}
        keepButtonTooltip={
          isKeepECDDisabled
            ? 'Your selected date will not meet the ECD. Please adjust ECD or choose another date.'
            : ''
        }
      />
    </>
  )
}

export default MilestoneDateTemplate
